import { atomFamily, DefaultValue } from "recoil";
import { selector, selectorFamily } from "./recoilSelectors";
import { atomWithPersistence, storageEffectForKey } from "../utils/persistence";
import { EMPTY_FILTER_SELECTION, FilterSelection } from "../types/filters";
import { activeFilterKeys, passesFilters } from "../utils/filters";
import { NO_TEAM, PEOPLE_FILTER, peopleFilterValues } from "../utils/peopleFilters";
import { pageState } from "./appState";
import { PAGE } from "../constants/pages";
import { Uid, UidNameUsername } from "../types/core";
import { memberNamesState, memberSearchState, memberState } from "./membersState";
import { containsSubstring } from "../utils/filter";
import { memberLiveBadgeState } from "./memberLiveBadgeState";
import { jobsAssignedState } from "./jobsState";
import { teamsState } from "./teamsState";
import { filterClockState } from "./filterClockState";
import { timezoneState } from "./accountState";

const TIMED_PEOPLE_FILTERS: string[] = ["status", "working", "attention", "active", "service"];

/** Kept per place like status: Live's quick toggles mean nothing on a bar that doesn't show them. */
const PER_PLACE_KEYS: string[] = [PEOPLE_FILTER.STATUS, PEOPLE_FILTER.ACTIVE, PEOPLE_FILTER.SERVICE];

/** Each place with a people filter bar remembers its own status picks; everything else is shared. */
export type PeopleFilterScope = "live" | "dispatch" | "heatmap" | "assignee";

/** Team, skill, role, jobs, working hours and attention: the same on every page that lists people. */
export const peopleSharedFiltersState = atomWithPersistence<FilterSelection>("filters.people", EMPTY_FILTER_SELECTION);

/** Moving on Live means nothing on Dispatch, so status is kept per place. */
export const peopleStatusFilterState = atomFamily<string[], PeopleFilterScope>({
    key: "filters.people.status",
    default: [],
    effects: (scope) => [storageEffectForKey(`filters.people.status.${scope}`)],
});

/** Each place's own quick toggles (Live's Active and In service), by filter. */
export const peopleQuickFilterState = atomFamily<Record<string, string[]>, PeopleFilterScope>({
    key: "filters.people.quick",
    default: {},
    effects: (scope) => [storageEffectForKey(`filters.people.quick.${scope}`)],
});

/** The whole selection one place applies: the shared filters plus its own status and quick toggles. */
export const peopleFilterSelectionState = selectorFamily<FilterSelection, PeopleFilterScope>({
    key: "filters.people.selection",
    get: (scope) => ({ get }) => {
        const shared = get(peopleSharedFiltersState);
        const status = get(peopleStatusFilterState(scope));
        const quick = Object.fromEntries(Object.entries(get(peopleQuickFilterState(scope))).filter(([, values]) => values.length > 0));
        return status.length > 0 || Object.keys(quick).length > 0
            ? { values: { ...shared.values, ...quick, ...(status.length > 0 ? { [PEOPLE_FILTER.STATUS]: status } : {}) }, added: shared.added }
            : shared;
    },
    set: (scope) => ({ set, reset }, value) => {
        if (value instanceof DefaultValue) {
            reset(peopleSharedFiltersState);
            reset(peopleStatusFilterState(scope));
            reset(peopleQuickFilterState(scope));
            return;
        }
        const { [PEOPLE_FILTER.STATUS]: status, [PEOPLE_FILTER.ACTIVE]: active, [PEOPLE_FILTER.SERVICE]: service, ...rest } = value.values;
        set(peopleStatusFilterState(scope), status ?? []);
        set(peopleQuickFilterState(scope), { [PEOPLE_FILTER.ACTIVE]: active ?? [], [PEOPLE_FILTER.SERVICE]: service ?? [] });
        set(peopleSharedFiltersState, { values: rest, added: value.added.filter(key => !PER_PLACE_KEYS.includes(key)) });
    },
});

/** The bar's scope on the current page; pages without a people bar apply no people filters. */
export const peopleFilterScopeForPageState = selector<PeopleFilterScope | undefined>({
    key: "filters.people.scope.page",
    get: ({ get }) => {
        switch (get(pageState)) {
            case PAGE.LIVE:
                return "live";
            case PAGE.DISPATCH:
                return "dispatch";
            case PAGE.HEATMAP:
                return "heatmap";
            default:
                return undefined;
        }
    },
});

export const assignedUsernamesState = selector<Set<string>>({
    key: "filters.people.assigned",
    get: ({ get }) => new Set(get(jobsAssignedState).map(job => job.assigneeUsername)),
});

export const teamsByIdState = selector({
    key: "filters.people.teams.byid",
    get: ({ get }) => new Map(get(teamsState).map(team => [team.id, team])),
});

/** Whether one person passes a place's people filters (search is separate, see memberIsVisibleState). */
export const memberPassesPeopleFiltersState = selectorFamily<boolean, { uid: Uid, scope: PeopleFilterScope }>({
    key: "filters.people.passes",
    get: ({ uid, scope }) => ({ get }) => {
        const selection = get(peopleFilterSelectionState(scope));
        const keys = activeFilterKeys(selection);
        if (keys.length === 0) {
            return true;
        }
        const member = get(memberState(uid));
        if (!member) {
            return false;
        }
        // read only what the active filters need, so a GPS update or a badge refresh only
        // re-evaluates people when a filter that depends on it is on
        // status, working hours and phone findings move with the clock, not only with the person's record
        const timed = keys.some(key => TIMED_PEOPLE_FILTERS.includes(key));
        const values = peopleFilterValues(member, {
            badge: keys.includes(PEOPLE_FILTER.STATUS) ? get(memberLiveBadgeState(uid)) : undefined,
            assignedUsernames: keys.includes(PEOPLE_FILTER.JOBS) ? get(assignedUsernamesState) : undefined,
            teamsById: keys.includes(PEOPLE_FILTER.WORKING) ? get(teamsByIdState) : undefined,
            now: timed ? Math.max(get(filterClockState), Date.now()) : Date.now(),
            timezone: keys.includes(PEOPLE_FILTER.WORKING) ? get(timezoneState) : undefined,
        }, keys);
        return passesFilters(values, selection);
    },
});

/** The teams picked in the Team filter, for lists that group people by team. */
export const peopleTeamFilterIdsState = selector<number[]>({
    key: "filters.people.teamids",
    get: ({ get }) => {
        const scope = get(peopleFilterScopeForPageState);
        if (!scope) {
            return [];
        }
        const teams: string[] = get(peopleFilterSelectionState(scope)).values[PEOPLE_FILTER.TEAM] ?? [];
        return teams.filter(team => team !== NO_TEAM).map(team => parseInt(team, 10));
    },
});

/** The people a place lists: the people search plus its filters. */
export const peopleVisibleNamesState = selectorFamily<UidNameUsername[], PeopleFilterScope>({
    key: "filters.people.visible",
    get: (scope) => ({ get }) => {
        const search = get(memberSearchState).toLowerCase();
        return get(memberNamesState).filter(({ uid, name, username }) =>
            (!search || containsSubstring(name.toLowerCase(), search) || containsSubstring(username, search))
            && get(memberPassesPeopleFiltersState({ uid, scope })));
    },
});
